class BceHeaderVideo {
    constructor() {
        this.instances = new Map();
    }

    create(element, options = {}) {
        if (this.instances.has(element)) {
            return this.instances.get(element);
        }

        const { videoUrl, imageUrl } = this.extractVideoAndImage(element);
        if (!videoUrl) {
            console.warn('BCE Header Video: No video URL found');
            return null;
        }

        const videoId = this.extractVideoId(videoUrl);
        if (!videoId) {
            console.warn('BCE Header Video: Invalid Wistia URL format');
            return null;
        }

        // Create structure with custom image if provided
        this.createOriginalStructure(element, videoId, imageUrl);
        
        // Mark as initialized after a short delay to prevent flash
        setTimeout(() => {
            element.classList.add('video-initialized');
        }, 100);

        const instance = { element, videoId, videoUrl, imageUrl };
        this.instances.set(element, instance);
        return instance;
    }

    extractVideoAndImage(element) {
        const textContent = element.textContent.trim();
        const lines = textContent.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        
        let videoUrl = null;
        let imageUrl = null;
        
        lines.forEach(line => {
            // Check for Wistia video URL
            if ((line.includes('wistia.com') || line.includes('wi.st')) && line.startsWith('http')) {
                videoUrl = line;
            }
            // Check for image URL (jpg, png, webp, etc.)
            else if (line.startsWith('http') && /\.(jpg|jpeg|png|webp|gif)$/i.test(line)) {
                imageUrl = line;
            }
        });
        
        return { videoUrl, imageUrl };
    }

    extractVideoId(url) {
        const patterns = [
            /wistia\.com\/medias\/([a-zA-Z0-9]+)/,
            /wi\.st\/([a-zA-Z0-9]+)/,
            /medias\/([a-zA-Z0-9]+)/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    createOriginalStructure(element, videoId, imageUrl = null) {
        // Load scripts first (EXACT original)
        this.loadOriginalScripts(videoId);
        
        // Use custom image URL if provided, otherwise fallback to Wistia default
        const thumbnailUrl = imageUrl || `https://embed-ssl.wistia.com/deliveries/${videoId}/file.jpg`;
        
        // Create EXACT original HTML structure with custom/default thumbnail
        element.innerHTML = `
            <div class="video-container">
                <div class="wistia_embed wistia_async_${videoId} autoPlay=true silentAutoPlay=true endVideoBehavior=loop controlsVisibleOnLoad=false playButton=false smallPlayButton=false bigPlayButton=false playbar=false volumeControl=false qualityControl=false copyLinkAndThumbnailEnabled=false muted=true playsinline=true" 
                     style="height:100%;position:relative;width:100%;pointer-events:none;" 
                     oncontextmenu="return false;">
                    <div class="wistia_swatch" style="height:100%;left:0;opacity:0;overflow:hidden;position:absolute;top:0;transition:opacity 200ms;width:100%;">
                        <img src="${thumbnailUrl}" 
                             style="height:100%;object-fit:cover;width:100%;" 
                             alt="" aria-hidden="true" onload="this.parentNode.style.opacity=1;" />
                    </div>
                </div>
            </div>
        `;
        
        // Setup EXACT original quality control
        this.setupOriginalQuality(videoId);
        
        // Fix Wistia after it loads and overwrites our HTML
        this.fixWistiaAfterLoad(videoId, thumbnailUrl);
    }

    loadOriginalScripts(videoId) {
        // EXACT original scripts
        if (!document.querySelector(`script[src*="${videoId}.jsonp"]`)) {
            const jsonpScript = document.createElement('script');
            jsonpScript.src = `https://fast.wistia.com/embed/medias/${videoId}.jsonp`;
            jsonpScript.async = true;
            jsonpScript.setAttribute('data-cfasync', 'false');
            document.head.appendChild(jsonpScript);
        }

        if (!document.querySelector('script[src*="E-v1.js"]')) {
            const wistiaScript = document.createElement('script');
            wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js';
            wistiaScript.async = true;
            wistiaScript.setAttribute('data-cfasync', 'false');
            document.head.appendChild(wistiaScript);
        }
    }

    setupOriginalQuality(videoId) {
        // EXACT original quality control
        window._wq = window._wq || [];
        window._wq.push({
            [videoId]: {
                qualityMin: window.innerWidth <= 768 ? "224p" : "720p",
                qualityMax: window.innerWidth <= 768 ? "224p" : "4k",
                plugin: {
                    "cropFill": {
                        "src": "//fast.wistia.com/labs/crop-fill/plugin.js"
                    }
                }
            }
        });
    }

    fixWistiaAfterLoad(videoId, customThumbnailUrl) {
        // Wait for Wistia to load and then fix the thumbnail
        const checkAndFix = () => {
            const swatchImg = document.querySelector(`.wistia_async_${videoId} .wistia_swatch img`);
            if (swatchImg && customThumbnailUrl) {
                // Replace Wistia's tiny swatch with our custom image
                swatchImg.src = customThumbnailUrl;
                swatchImg.style.filter = 'none';
                console.log(`Fixed Wistia thumbnail for ${videoId}: ${customThumbnailUrl}`);
            } else if (swatchImg) {
                // Just remove blur from existing Wistia image
                swatchImg.style.filter = 'none';
                console.log(`Removed blur from Wistia thumbnail for ${videoId}`);
            } else {
                // Keep checking until Wistia loads
                setTimeout(checkAndFix, 100);
            }
        };
        
        // Start checking after a delay
        setTimeout(checkAndFix, 500);
    }

    destroy(element) {
        const instance = this.instances.get(element);
        if (instance) {
            element.innerHTML = '';
            this.instances.delete(element);
        }
    }

    getInstance(element) {
        return this.instances.get(element);
    }
}

export default new BceHeaderVideo();